import { ChangeEvent, FC, useState } from "react";
import { z } from "zod";

export const phoneSchema = z
  .string()
  .refine(
    (val) => {
      const local = val.slice(2);
      return /^[1-9]/.test(local);
    },
    {
      message: "Le numéro doit commencer par un chiffre entre 1 et 9",
    }
  )
  .refine(
    (val) => {
      const local = val.slice(2); // on enlève l'indicatif 33
      return local.length === 9;
    },
    {
      message: "Le numéro doit contenir exactement 9 chiffres (hors +33)",
    }
  );

interface PhoneInputProps {
  value?: string;
  onChange?: (value: string) => void;
  error?: string;
}

const countries = [{ code: "fr", name: "France", dialCode: "33" }] as const;

type Country = (typeof countries)[number];

const PhoneInput: FC<PhoneInputProps> = ({ value = "", onChange, error }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<Country>(
    countries.find((c) => value.startsWith(c.dialCode)) || countries[0]
  );
  const [validationError, setValidationError] = useState<string>("");

  const localNumber = value.startsWith(selectedCountry.dialCode)
    ? value.slice(selectedCountry.dialCode.length)
    : "";

  const validatePhone = (phoneValue: string) => {
    if (!phoneValue || phoneValue.length <= 2) {
      setValidationError("");
      return;
    }

    try {
      phoneSchema.parse(phoneValue);
      setValidationError("");
    } catch (err) {
      if (err instanceof z.ZodError) {
        setValidationError(err.errors[0]?.message || "");
      }
    }
  };

  const handlePhoneChange = (e: ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, "");
    const newValue = selectedCountry.dialCode + digits;
    onChange?.(newValue);
    validatePhone(newValue);
  };

  const handleCountrySelect = (country: Country) => {
    setSelectedCountry(country);
    setDropdownOpen(false);
    const digits = localNumber.replace(/\D/g, "");
    const newValue = country.dialCode + digits;
    onChange?.(newValue);
    validatePhone(newValue);
  };

  const displayError = validationError || error;

  return (
    <div className="flex flex-col">
      <label className="mb-1 text-sm font-medium">Numéro de téléphone *</label>

      <div className="relative flex items-center border rounded-full focus-within:ring-2 focus-within:ring-blue-500">
        {/* Drapeau & bouton dropdown */}
        <div
          onClick={() => setDropdownOpen(!dropdownOpen)}
          className="flex items-center px-3 py-2 border-r cursor-pointer"
        >
          <img
            src={`https://flagcdn.com/w40/${selectedCountry.code}.png`}
            alt={selectedCountry.name}
            className="w-5 h-4"
          />
        </div>

        {/* Liste déroulante personnalisée */}
        {dropdownOpen && (
          <ul className="absolute left-0 z-50 mt-1 overflow-auto bg-white border rounded-md shadow-md top-full w-52 max-h-60">
            {countries.map((country) => (
              <li
                key={country.code}
                onClick={() => handleCountrySelect(country)}
                className="flex items-center px-3 py-2 cursor-pointer hover:bg-gray-100"
              >
                <img
                  src={`https://flagcdn.com/w40/${country.code}.png`}
                  alt={country.name}
                  className="w-5 h-4 mr-2"
                />
                <span>{country.name}</span>
              </li>
            ))}
          </ul>
        )}

        {/* Code pays affiché */}
        <span className="px-3 text-sm text-gray-600 whitespace-nowrap">+{selectedCountry.dialCode}</span>

        {/* Champ numéro */}
        <input
          type="text"
          placeholder="123456789"
          value={localNumber}
          onChange={handlePhoneChange}
          maxLength={9}
          className="flex-1 px-4 py-2.5 bg-white border-none focus:outline-none text-sm"
        />
      </div>

      {displayError && <span className="mt-1 text-xs text-red-500">{displayError}</span>}
    </div>
  );
};

export default PhoneInput;
